import {
  useLoaderData,
  useRouteLoaderData,
  useParams,
  useNavigate,
  useRevalidator,
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import VisibilityIcon from '@mui/icons-material/Visibility'
import NewspaperIcon from '@mui/icons-material/Newspaper'
import EditIcon from '@mui/icons-material/Edit'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CloudDoneIcon from '@mui/icons-material/CloudDone'
import { GridColDef, GridInitialState } from '@mui/x-data-grid-premium'

import StripedDataGrid from '../../../root/components/StripedDataGrid'
import AbstractStatusTag from '../../components/AbstractStatusTag'
import { LoaderData, AbstractStatus, Abstracts } from '../../../../types'
import { listAbstractsLoader, abstractSubmitListEventsLoader } from '../../Abstracts.loader'
import {
  useAbstractStatusOptions,
  useThemesDataOptions,
  useDomainsDataOptions,
} from '../../../../utils'
import { useSubmitAbstractMutation } from '../../../../services/events'
import { toast } from 'react-toastify'

const initialState: GridInitialState = {
  columns: {
    columnVisibilityModel: {
      id: false,
    },
  },
  sorting: {
    sortModel: [
      {
        field: 'status',
        sort: 'desc',
      },
    ],
  },
}

export default function AbstractsSubmitList() {
  const { t } = useTranslation('abstracts')
  const { eventId } = useParams()
  const navigate = useNavigate()
  const revalidator = useRevalidator()
  const abstractsList = useLoaderData() as LoaderData<ReturnType<typeof listAbstractsLoader>>
  const abstracts = abstractsList?.results ?? []
  const events = useRouteLoaderData('submit-abstracts-list') as LoaderData<
    ReturnType<typeof abstractSubmitListEventsLoader>
  >

  const statusOptions = useAbstractStatusOptions(t)
  const themesDataOptions = useThemesDataOptions()
  const domainsDataOptions = useDomainsDataOptions()

  const [submitAbstract] = useSubmitAbstractMutation()

  const event = useMemo(
    () => events?.find(event => event.id === Number(eventId)),
    [eventId, events]
  )

  const columns: GridColDef<Abstracts>[] = useMemo(
    () => [
      {
        field: 'actions',
        headerName: t('Actions'),
        type: 'actions',
        width: 100,
        filterable: false,
        disableExport: true,
        renderCell: params => {
          const row = params.row
          return (
            <Stack direction="row">
              <Tooltip title={row.status !== AbstractStatus.Draft ? t('Voir') : t('Modifier')}>
                <IconButton
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()

                    navigate(`/abstracts/list/${eventId}/abstract/${row.id}/submit`)
                  }}
                >
                  {row.status !== AbstractStatus.Draft ? <VisibilityIcon /> : <EditIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title={t('Soumettre')}>
                <IconButton
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()

                    if (window.confirm(t('Êtes-vous sûr de vouloir soumettre ce résumé ?'))) {
                      submitAbstract({ eventId: Number(eventId), id: row.id })
                        .then(data => {
                          revalidator.revalidate()
                          if ('data' in data) {
                            toast.success(t('Résumé soumis avec succès'))
                          }
                        })
                        .catch(() => {
                          toast.error(t('Erreur lors de la soumission du résumé'))
                        })
                    }
                  }}
                  disabled={row.status !== AbstractStatus.Draft}
                  color="primary"
                >
                  {row.status !== AbstractStatus.Draft ? <CloudDoneIcon /> : <UploadFileIcon />}
                </IconButton>
              </Tooltip>
            </Stack>
          )
        },
      },
      {
        field: 'title',
        headerName: t('Titre du résumé'),
        width: 500,
      },
      {
        field: 'domain',
        headerName: t('Domaine'),
        width: 150,
        type: 'singleSelect',
        valueOptions: domainsDataOptions,
      },
      {
        field: 'theme',
        headerName: t('Thème'),
        width: 250,
        type: 'singleSelect',
        valueOptions: themesDataOptions,
      },
      {
        field: 'status',
        headerName: t('État'),
        width: 150,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: statusOptions,
        renderCell: params => (
          <AbstractStatusTag size="small" status={params.value as Abstracts['status']} />
        ),
      },
    ],
    [
      domainsDataOptions,
      eventId,
      navigate,
      revalidator,
      statusOptions,
      submitAbstract,
      t,
      themesDataOptions,
    ]
  )

  return (
    <Stack flex="1 0" margin="1rem" sx={{ overflowY: 'auto' }}>
      {eventId === undefined || event === undefined ? (
        <Stack flex="1 0 auto" justifyContent="center" alignItems="center">
          <Typography variant="h2">{t('Veuillez sélectionner un événement valide')}</Typography>
        </Stack>
      ) : (
        <Stack flex="1 0 auto" gap="1rem">
          <Typography variant="h2">
            {t('Vos résumés pour')} {event?.name}
          </Typography>
          <Divider sx={{ margin: 0 }} />
          <Stack direction={'row'} gap="1rem">
            <Button fullWidth variant="outlined" startIcon={<NewspaperIcon />}>
              {t('Consulter les recommandations aux auteurs')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/abstracts/list/${eventId}/abstract/new/submit`)}
            >
              {t('Saisir un nouveau résumé')}
            </Button>
          </Stack>

          <Stack flexGrow="1">
            <StripedDataGrid
              rows={abstracts}
              columns={columns}
              count={abstractsList?.count}
              paginationMode="client"
              initialState={initialState}
              hideExport
              checkboxSelection={false}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}
